/** Multi-location rules, kept pure so they can be tested and so every surface agrees.
 *
 *  The governing rule is progressive disclosure: a clinic with one location must see no
 *  location chrome at all — no picker, no filter, no column, no extra step. Most clinics
 *  have one site and should not pay for a feature they don't use. Everything here exists
 *  so that "should this appear?" has exactly one answer in one place.
 */

export function activeLocations(locations) {
  return (locations || []).filter((l) => l && l.active !== false);
}

/** The single switch every piece of location chrome hangs off. */
export function isMultiLocation(locations) {
  return activeLocations(locations).length >= 2;
}

/** Distinct locations present in an availability response, in first-seen order.
 *
 *  Derived from the slots themselves rather than the clinic's location list, because
 *  what matters when booking is where this visit type is actually offered — a clinic can
 *  have four sites and still run IV therapy at only one of them.
 */
export function locationsFromSlots(slots) {
  const seen = new Map();
  (slots || []).forEach((s) => {
    if (s && s.location_id && !seen.has(s.location_id)) {
      seen.set(s.location_id, { id: s.location_id, name: s.location_name || "" });
    }
  });
  return [...seen.values()];
}

/** Filter slots to one location. An empty/absent id means "all", not "none". */
export function filterSlotsByLocation(slots, locationId) {
  if (!locationId) return slots || [];
  return (slots || []).filter((s) => s.location_id === locationId);
}

/** Whether a booking flow must ask which site. Only when the choice is real. */
export function needsLocationChoice(slots) {
  return locationsFromSlots(slots).length >= 2;
}

/** Name (+ address) for display, skipping the separator when there is no address. */
export function locationLabel(location) {
  if (!location) return "";
  return [location.name, location.address].filter(Boolean).join(" · ");
}
